import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  subscriptionService,
  type SubscriptionCreate,
  type SubscriptionUpdate,
  type ServiceName,
} from '@/services/subscription'

// List subscriptions
export const useSubscriptions = (params?: {
  organization_id?: string
  service_name?: ServiceName
}) => {
  return useQuery({
    queryKey: ['subscriptions', params],
    queryFn: () => subscriptionService.listSubscriptions(params),
  })
}

// Get subscription
export const useSubscription = (subscriptionId: string) => {
  return useQuery({
    queryKey: ['subscription', subscriptionId],
    queryFn: () => subscriptionService.getSubscription(subscriptionId),
    enabled: !!subscriptionId,
  })
}

// Create subscription
export const useCreateSubscription = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SubscriptionCreate) =>
      subscriptionService.createSubscription(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Update subscription
export const useUpdateSubscription = (subscriptionId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SubscriptionUpdate) =>
      subscriptionService.updateSubscription(subscriptionId, data),
    onSuccess: (data) => {
      queryClient.setQueryData(['subscription', subscriptionId], data)
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Cancel subscription
export const useCancelSubscription = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, cancelAtPeriodEnd }: { id: string; cancelAtPeriodEnd?: boolean }) =>
      subscriptionService.cancelSubscription(id, {
        cancel_at_period_end: cancelAtPeriodEnd ?? true,
      }),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['subscription', variables.id], data)
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Reactivate subscription
export const useReactivateSubscription = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => subscriptionService.reactivateSubscription(id),
    onSuccess: (data, id) => {
      queryClient.setQueryData(['subscription', id], data)
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Get available plans
export const usePlans = (serviceName?: ServiceName) => {
  return useQuery({
    queryKey: ['plans', serviceName],
    queryFn: () => subscriptionService.getPlans(serviceName),
  })
}

